<template>
  <RevCard
    v-if="review"
    ref="reviewContainer"
    v-bind="ariaLabel ? { 'aria-label': ariaLabel } : {}"
    class="isolate h-full w-full cursor-pointer max-h-[var(--carousel-max-size)]"
    data-test="review-card"
    @click="(openUserCommentModal(), onCardClick())"
    @keydown.enter="(openUserCommentModal(), onCardClick())"
  >
    <div class="relative h-full w-full overflow-hidden rounded-lg">
      <RevIllustration
        alt=""
        class="block w-full rounded-lg object-cover aspect-square"
        :height="480"
        loading="lazy"
        :src="review.pictures?.[0] || ''"
        :width="480"
      />

      <RevTag
        class="absolute left-16 top-16"
        data-allow-mismatch
        :label="reviewDate"
        variant="secondary"
      />

      <div
        class="mood-inverse absolute bottom-0 left-0 right-0 flex flex-col gap-8"
      >
        <div class="isolate z-[1] flex justify-between gap-8 p-20 rounded-b-lg">
          <RevRating :score="review.averageRate" size="extra-small" />
          <span
            class="text-static-default-hi caption-bold ml-auto mr-0 flex items-center"
          >
            <span>
              {{ review.customer?.firstName }}
              {{ review.customer?.lastName }}
            </span>
            <IconOfficialFilled class="ml-4" size="16" />
          </span>
        </div>

        <div
          class="absolute bottom-0 left-0 right-0 z-0 h-full rounded-b-lg"
          :class="gradientClass"
        />
      </div>
    </div>
  </RevCard>
  <ClientOnly>
    <UserCommentModal
      :key="resolvedModalName"
      defer
      :modal-name="resolvedModalName"
      :pictures="review?.pictures || []"
      :review
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, useTemplateRef } from 'vue'

import { formatDifference } from '@backmarket/nuxt-layer-cms/DynamicTextDates.utils'
import UserCommentModal from '@backmarket/nuxt-layer-reviews/review-display/ReviewModal.vue'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'
import { tw } from '@ds/utils/tailwind'
import { useIntersectionObserver } from '@vueuse/core'

import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'

const props = defineProps<{
  review: ReviewWithCount
  ariaLabel?: string
}>()

const emit = defineEmits<{
  (e: 'visible'): void
  (e: 'click'): void
}>()

const currentLocale = useI18nLocale()

const reviewDate = computed(() => {
  const differenceInDays =
    (new Date(props.review.reviewDate).getTime() - new Date().getTime()) /
    (1000 * 60 * 60 * 24)

  return formatDifference(differenceInDays, currentLocale)
})

const gradientClass = tw`
  bg-[linear-gradient(180deg,rgba(15,17,23,0)_0%,rgba(15,17,23,0.56)_30.29%,rgba(15,17,23,0.6)_100%)]
  after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:!top-[initial]
  after:h-full after:pointer-events-none after:backdrop-blur-[16px]
  after:[mask:linear-gradient(180deg,transparent_0%,#000_30%,#000)]
  after:rounded-bl-[0.75rem] after:rounded-br-[0.75rem]
`

function onCardClick() {
  emit('click')
}

const resolvedModalName = computed(
  () => `REVIEW_IN_IMAGE_CAROUSEL_${props.review.id}`,
)

function openUserCommentModal() {
  openModal(resolvedModalName.value)
}

onBeforeUnmount(() => closeModal())

const reviewContainer = useTemplateRef('reviewContainer')
const { stop } = useIntersectionObserver(
  reviewContainer,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting && intersectionRatio >= 0.7) {
      emit('visible')
      stop()
    }
  },
  {
    threshold: 0.7,
  },
)
</script>
